import { useContext } from 'react'

import { AppConfigContext } from './context'

/**
 * Returns whether a feature flag is enabled.
 *
 * Reactive: values update when the underlying config provider syncs new data
 * from the remote source. The returned boolean is safe to use in dependency
 * arrays.
 */
export function useFeatureFlag(name: string): boolean {
  const ctx = useContext(AppConfigContext)
  return ctx.checkFeatureFlag(name)
}

/**
 * Registry mapping remote-config names to their typed payload shapes.
 *
 * Consumers augment this interface in their own module to get inferred return
 * types from `useRemoteConfig` without casting at call sites:
 *
 * ```ts
 * declare module '@moment/app-config-ui' {
 *   interface RemoteConfigMap {
 *     'my_config': MyConfigShape
 *   }
 * }
 * ```
 *
 * Unregistered names fall back to `Record<string, unknown>`.
 */
export interface RemoteConfigMap {
  /** Placeholder so the interface is non-empty; augmented entries override via declaration merging. */
  readonly __placeholder?: never
}

type RegisteredRemoteConfigName = Exclude<
  keyof RemoteConfigMap,
  '__placeholder'
> &
  string

/**
 * Returns the key-value pairs of a remote config.
 *
 * Reactive: values update when the underlying config provider syncs new data
 * from the remote source.
 *
 * Note: each call returns a new object reference. Depend on specific properties
 * rather than the object itself when using it in dependency arrays.
 */
export function useRemoteConfig<K extends RegisteredRemoteConfigName>(
  name: K,
): RemoteConfigMap[K]
export function useRemoteConfig(name: string): Record<string, unknown>
export function useRemoteConfig(name: string): Record<string, unknown> {
  const ctx = useContext(AppConfigContext)
  return ctx.getRemoteConfig(name)
}
